import { FunctionComponent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { translateRequestError } from "@/utils/errors";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { buildCreditBreakdown, CreditSegmentView } from "./creditBreakdown";
import { CreditCostBlock } from "./CreditCostBlock";
import { CreditTopUp } from "./CreditTopUp";
import { useCreditBalance } from "./useCreditBalance";

export interface CreditConfirmModalProps {
	isOpened: boolean;
	/** Verb and object, no count: "Find emails". */
	title: string;
	/** States the count or names the lead. */
	body: ReactNode;
	/** Repeats the verb with the count: "Find emails for 142 leads". */
	confirmLabel: string;
	/** The selection's parts; undefined while the quote loads. */
	segments: CreditSegmentView[] | undefined;
	/** What the API needs the balance to hold to start, when more than the known total (M2). */
	requiredToStart?: number;
	/** The quote request's failure; the action is not offered without its cost. */
	quoteError?: unknown;
	/** What to say about `quoteError`, when the caller knows better than the generic sentence. */
	quoteErrorMessage?: string;
	/** How the action charges. */
	chargeNote?: string;
	/** Controls that change the scope, e.g. the rescore toggle, shown above the cost. */
	children?: ReactNode;
	/**
	 * Whether a 402 refusal means nothing was charged. True for every action that checks the
	 * balance before its first charge.
	 */
	nothingChargedOnRefusal?: boolean;
	onClose: () => void;
	/** Runs the action; rejects with the request's error when the API refuses it. */
	onConfirm: () => Promise<unknown>;
}

const statusOf = (error: unknown): unknown =>
	error && typeof error === "object" && "status" in error ? error.status : undefined;

/**
 * The one confirm every credit-spending action goes through (PREC-4263): title, what it covers,
 * the cost against the balance (`CreditCostBlock`), and a button that repeats the verb. The button
 * waits for the balance and the quote, is disabled when a loaded balance does not cover the total,
 * runs the action once however often it is clicked (`useSingleFlight`), and a refusal is shown
 * here, in the dialog, with what it charged.
 */
export const CreditConfirmModal: FunctionComponent<CreditConfirmModalProps> = ({
	isOpened,
	title,
	body,
	confirmLabel,
	segments,
	requiredToStart,
	quoteError,
	quoteErrorMessage,
	chargeNote,
	children,
	nothingChargedOnRefusal = true,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const balance = useCreditBalance();
	const { run, isRunning } = useSingleFlight();
	const [refusal, setRefusal] = useState<unknown>(null);

	// A close while the action runs would hide its answer, so the dialog stays until it is in.
	const close = () => {
		if (isRunning) {
			return;
		}

		setRefusal(null);
		onClose();
	};

	const breakdown = segments
		? buildCreditBreakdown(segments, balance.balance, { requiredToStart })
		: null;
	const isRefusedForCredits = statusOf(refusal) === 402;

	const confirm = () =>
		run(async () => {
			setRefusal(null);

			try {
				await onConfirm();
			} catch (error) {
				setRefusal(error ?? new Error("refused"));
			}
		});

	return (
		<Modal
			takeFocus
			disableSave={!breakdown?.canConfirm || !!quoteError}
			handleClose={close}
			handleSave={confirm}
			isLoading={isRunning}
			isOpened={isOpened}
			size="xs"
			submitButtonText={isRunning ? t("creditConfirm.pleaseWait") : confirmLabel}
			title={title}
		>
			<div className="flex flex-col gap-4" data-test="credit-confirm-modal">
				{refusal !== null && (
					<p className="text-sm text-status-error" data-test="credit-confirm-refusal" role="alert">
						{t(
							isRefusedForCredits && nothingChargedOnRefusal
								? "creditConfirm.refusedNothingCharged"
								: "creditConfirm.failed",
							{ reason: translateRequestError(refusal) }
						)}{" "}
						{isRefusedForCredits && (
							<CreditTopUp hasSubscription={balance.hasSubscription} onClosed={balance.refetch} />
						)}
					</p>
				)}
				<p className="text-sm text-text">{body}</p>
				{children}
				{quoteError ? (
					<p className="text-sm text-status-error" data-test="credit-quote-failed">
						{quoteErrorMessage ??
							t("creditConfirm.quoteFailed", { reason: translateRequestError(quoteError) })}
					</p>
				) : breakdown ? (
					<>
						<CreditCostBlock balance={balance} breakdown={breakdown} chargeNote={chargeNote} />
						{breakdown.workCount === 0 && (
							<p className="text-xs text-text-meta">{t("creditConfirm.nothingToRun")}</p>
						)}
					</>
				) : (
					<div
						className="h-16 animate-pulse rounded bg-border-light"
						data-test="credit-quote-loading"
					/>
				)}
			</div>
		</Modal>
	);
};
